import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/new-article.css';
import api from '../api';
import Icon from '../components/Icon';
import { PageHead, Spinner } from '../components/ui';
import { confirmedStageIndex, stageStates, progressDisplay, nextPollHealth, jobPhase } from '../lib/jobProgress';
import { saveJobHandle, readJobHandle, clearJobHandle, isResumable,
         stopWaitingCopy, resumeCopy } from '../lib/jobLifecycle';
import { planRefusal, contactHref } from '../lib/planCopy';
import { usePlan } from '../lib/queries';
import Recorder from '../components/Recorder';
import DriveImportButton from '../components/DriveImportButton';
import { DRIVE_IMPORT_ENABLED } from '../featureFlags';
import PhoneRecord from '../components/PhoneRecord';
import FigmaImportPanel from '../components/FigmaImportPanel';
import { useBrand } from '../lib/BrandContext';
import { useWhisperCheck, useDetailLevel } from '../lib/queries';

const STEPS_LOOM = [
  { id: 'download', label: 'Downloading video from Loom' },
  { id: 'transcript', label: 'Fetching transcript' },
  { id: 'ai', label: 'Structuring steps with Flow' },
  { id: 'save', label: 'Saving draft' },
];

const STEPS_VIDEO = [
  { id: 'copy', label: 'Loading video file' },
  { id: 'transcribe', label: 'Transcribing with Affixly (this takes a minute)' },
  { id: 'ai', label: 'Structuring steps with Flow' },
  { id: 'save', label: 'Saving draft' },
];

// Used when user picks a file — upload step is real progress, then auto-processes
const STEPS_UPLOAD_VIDEO = [
  { id: 'upload',     label: 'Uploading video' },
  { id: 'transcribe', label: 'Transcribing with Affixly (this takes a minute)' },
  { id: 'ai',         label: 'Structuring steps with Flow' },
  { id: 'save',       label: 'Saving draft' },
];

// Source methods for the documentation flow (Phone lives inside Record — it
// was deliberately demoted to a secondary path, GP-15).
const SOURCE_TABS = [
  { id: 'record', label: 'Record',       icon: 'record', recommended: true },
  { id: 'video',  label: 'Upload video', icon: 'upload' },
  { id: 'loom',   label: 'Loom URL',     icon: 'link' },
];

const DETAIL_OPTIONS = [
  { id: 'concise',  label: 'Concise',  desc: '3–5 broad steps, one sentence each.' },
  { id: 'standard', label: 'Standard', desc: '5–10 steps, 1–2 sentences each.' },
  { id: 'detailed', label: 'Detailed', desc: '8–15 granular steps, 2–3 sentences each.' },
];

// Client-side mirrors of the server's constraints, checked before anything is
// sent: /api/upload-video rejects > 500 MB (and Flask's MAX_CONTENT_LENGTH is
// the same), and extract_loom_id() only accepts loom.com/share/<id> or /v/<id>.
const VIDEO_ACCEPT = 'video/*,.mp4,.mov,.m4v,.avi,.webm';
const VIDEO_EXTS = ['.mp4', '.mov', '.m4v', '.avi', '.webm'];
const MAX_UPLOAD_BYTES = 500 * 1024 * 1024;
const LOOM_RE = /loom\.com\/(share|v)\/[a-f0-9]+/;

function validateVideoFile(file) {
  if (!file) return 'No file was selected.';
  const name = (file.name || '').toLowerCase();
  const extOk = VIDEO_EXTS.some(ext => name.endsWith(ext));
  const typeOk = (file.type || '').startsWith('video/');
  if (!extOk && !typeOk) return 'That file isn’t a supported video. Use MP4, MOV, M4V, AVI or WebM.';
  if (file.size > MAX_UPLOAD_BYTES) return 'That file is larger than 500 MB. Trim or compress it and try again.';
  return '';
}

export default function NewArticle({ onCreated, prefill, project }) {
  const { brand } = useBrand();
  const { data: whisperInstalled = null } = useWhisperCheck();
  const { data: serverDetailLevel } = useDetailLevel();
  // Review flow gets a lean Figma-only render with no source picker, no
  // detail-level picker, no "How it works" card. The signal is *intent*
  // (route state set by "+ New Review" sidebar item OR a project ref from
  // a project page), NOT the workspace persona — design-persona workspaces
  // still need the documentation flow when they click "+ New Article".
  // The documentation branch below the early-return is the future home for
  // product-persona Jira-as-input.
  const location = useLocation();
  // ?review=1 is the addressable form of the review flow. It was previously
  // carried only in React Router location.state, so a refresh, a bookmark or
  // the back button silently dropped the user into the documentation flow.
  // location.state is still honoured so in-app links keep working.
  const reviewParam = new URLSearchParams(location.search).get('review') === '1';
  const isReviewFlow = reviewParam || !!location.state?.reviewFlow || !!project;
  const [tab, setTab] = useState('record'); // 'video' | 'record' | 'loom' — documentation flow only
  const [recordMode, setRecordMode] = useState('browser'); // 'browser' | 'phone'

  // Loom state
  const [loomUrl, setLoomUrl] = useState('');
  const [loomTranscript, setLoomTranscript] = useState('');
  const [showLoomTranscript, setShowLoomTranscript] = useState(false);

  // Video state
  const [videoPath, setVideoPath] = useState('');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoTranscript, setVideoTranscript] = useState('');
  const [showVideoTranscript, setShowVideoTranscript] = useState(false);
  const [uploading] = useState(false); // legacy flag — the combined upload flow drives progress now
  const [uploadedName, setUploadedName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadFlow, setUploadFlow] = useState(false); // true = file-picker triggered combined upload+process

  // Shared
  const [processing, setProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState('');

  // Presentation-only: pre-submit validation + drop target + record hand-off guard
  const [loomTouched, setLoomTouched] = useState(false);
  const [fileError, setFileError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const handoffRef = useRef(false); // blocks a double hand-off from Recorder/PhoneRecord

  // GP-02: real background processing — polling, cancel, retry, "still running"
  const [bgArticleId, setBgArticleId] = useState(null); // known article id while processing
  const [stillRunning, setStillRunning] = useState(false); // polling shows job not finished
  // F04 — progress comes from the server, never from a timer.
  const [jobStatus, setJobStatus] = useState(null);        // null|'running'|'done'|'failed'
  const [pollInterrupted, setPollInterrupted] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);    // kept so the job stays resumable
  const pollHealthRef = useRef({ failures: 0, interrupted: false });
  // §1.3.2 — a 402 plan refusal is a complete explanation in itself, and it
  // always means no work started, so the inputs on screen are untouched.
  const [refusal, setRefusal] = useState(null);
  const { data: planData } = usePlan();
  const failureMessage = (err, fallback) => {
    const r = planRefusal(err);
    setRefusal(r);
    if (r) return r.message;
    return err?.response?.data?.error || fallback;
  };

  // F05 — a job left running is still reachable after a reload.
  const [resumeHandle, setResumeHandle] = useState(null);
  const lastSubmitRef = useRef(null);   // () => void  re-runs the same submit for Retry
  const abortRef = useRef(null);        // AbortController for the in-flight request
  const pollRef = useRef(null);         // setInterval handle for job polling
  const stepTimerRef = useRef(null);    // fake step-advance timer handle (so we can clear it)

  // Map backend processing_jobs.step → frontend checklist index (defensive fallbacks).
  // Backend steps (services/media.py): uploading_video, uploading_transcript,
  // generating_gifs, complete. These are post-AI persistence steps, so we map
  // them onto the tail of whichever checklist is active.
  const jobStepToIndex = confirmedStageIndex;   // F04 — shared with lib/jobProgress

  // Clear every timer/poller/abort we may have started.
  const clearAsync = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (stepTimerRef.current) { clearTimeout(stepTimerRef.current); stepTimerRef.current = null; }
  };

  // Cleanup on unmount.
  useEffect(() => () => { clearAsync(); if (abortRef.current) abortRef.current.abort(); }, []);

  // F05 — on arrival, offer back a job that was still running when we left.
  useEffect(() => {
    const h = readJobHandle(window.localStorage);
    if (isResumable(h)) setResumeHandle(h);
    else if (h) clearJobHandle(window.localStorage);
  }, []);

  // GP-02: poll GET /api/jobs/<job_id> to drive real progress.
  // Defensive: every field access is guarded; unknown shapes never throw and
  // never break the synchronous happy path.
  const startJobPolling = (jobId, total) => {
    if (!jobId) return;
    setActiveJobId(jobId);
    // F05 — durable handle, so closing the tab doesn't lose the job.
    saveJobHandle(window.localStorage, { jobId, articleId: bgArticleId, title: title || fileName || '' });
    pollHealthRef.current = { failures: 0, interrupted: false };
    setPollInterrupted(false);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/jobs/${jobId}`);
        pollHealthRef.current = nextPollHealth(pollHealthRef.current, { ok: true });
        setPollInterrupted(false);
        const job = res?.data || {};
        const status = job?.status;
        if (status) setJobStatus(status);
        const idx = jobStepToIndex(job?.step, total);
        if (idx !== null && idx !== undefined) {
          // Real progress wins over the fake timer once we have it.
          if (stepTimerRef.current) { clearTimeout(stepTimerRef.current); stepTimerRef.current = null; }
          setCurrentStep(idx);
        }
        if (job?.article_id && !bgArticleId) setBgArticleId(job.article_id);
        if (status === 'failed') {
          clearAsync();
          setError(job?.error || 'Processing failed');
          setProcessing(false);
          setStillRunning(false);
          setCurrentStep(0);
        } else if (status === 'done') {
          clearAsync();
          setStillRunning(false);
          setCurrentStep(total);
        } else if (status === 'running') {
          // Job still working — surface the honest "this can take a while" hint.
          setStillRunning(true);
        }
      } catch (e) {
        // F04 — a job row that does not exist yet is normal for the first few
        // ticks, but a sustained outage must be visible rather than swallowed.
        // The job itself is kept either way; we just stop claiming to know.
        pollHealthRef.current = nextPollHealth(pollHealthRef.current, { ok: false });
        setPollInterrupted(pollHealthRef.current.interrupted);
      }
    }, 2500);
  };

  // Continue in background: hand off to the editor for the known article while
  // processing keeps going server-side.
  const continueInBackground = () => {
    clearAsync();
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    const id = bgArticleId;
    setProcessing(false);
    setStillRunning(false);
    if (id) {
      clearJobHandle(window.localStorage);   // the draft is open; nothing to resume
      onCreated(id, videoPath || '', '');
    }
  };

  // Cancel: abort the in-flight request and restore the form (inputs are kept).
  // F05 — this stops the wait on this screen. It does NOT cancel the job:
  // Flow has no server-side cancellation (processing_jobs never leaves
  // pending/running/done/failed), so claiming otherwise would be a lie. The
  // handle is kept so the job stays reachable.
  const stopWaiting = () => {
    clearAsync();
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    if (activeJobId || bgArticleId) {
      saveJobHandle(window.localStorage, {
        jobId: activeJobId, articleId: bgArticleId, title: title || fileName || '',
      });
    }
    setProcessing(false);
    setStillRunning(false);
    setUploadFlow(false);
    setCurrentStep(0);
  };

  // Detail level — prefilled from workspace default, per-article override allowed
  const [detailLevel, setDetailLevel] = useState('standard');

  // Seed local detailLevel from the workspace default when it arrives.
  // Only seeds once (when serverDetailLevel transitions from undefined → value),
  // so per-article overrides aren't clobbered by background refetches.
  useEffect(() => {
    if (serverDetailLevel) setDetailLevel(serverDetailLevel);
  }, [serverDetailLevel]);

  // Apply prefill from Support Insights
  useEffect(() => {
    if (!prefill) return;
    if (prefill.title)   setVideoTitle(prefill.title);
    if (prefill.summary) { setVideoTranscript(prefill.summary); setShowVideoTranscript(true); }
  }, [prefill]);

  const handleProcessLoom = async () => {
    lastSubmitRef.current = handleProcessLoom; // GP-02: enable Retry
    setError(''); setRefusal(null);
    setProcessing(true);
    setStillRunning(false);
    setBgArticleId(null);
    setCurrentStep(0);
    clearAsync();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await api.post('/process-loom', {
        loom_url: loomUrl.trim(),
        transcript: loomTranscript.trim(),
        detail_level: detailLevel,
        brand,
      }, { signal: ctrl.signal });
      clearAsync();
      if (res.data.article_id) setBgArticleId(res.data.article_id);
      // Synchronous happy path already returned the finished article.
      setCurrentStep(STEPS_LOOM.length);
      setTimeout(() => onCreated(res.data.article_id, res.data.video_path || '', res.data.job_id || ''), 500);
    } catch (err) {
      clearAsync();
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return; // user cancelled
      setError(failureMessage(err, 'Something went wrong'));
      setProcessing(false);
      setStillRunning(false);
      setCurrentStep(0);
    }
  };

  const handleProcessVideo = async () => {
    lastSubmitRef.current = handleProcessVideo; // GP-02: enable Retry
    setError(''); setRefusal(null);
    setProcessing(true);
    setStillRunning(false);
    setBgArticleId(null);
    setCurrentStep(0);
    clearAsync();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    // Whisper takes longer — slower step progression (fake in-flight progress;
    // real progress takes over once a job_id starts polling).

    try {
      const res = await api.post('/process-video', {
        video_path: videoPath.trim(),
        title: videoTitle.trim() || 'Untitled recording',
        transcript: videoTranscript.trim(),
        detail_level: detailLevel,
        brand,
      }, { timeout: 600000, signal: ctrl.signal }); // 10 min timeout for long videos
      if (res.data.article_id) setBgArticleId(res.data.article_id);
      // If the request resolved but the job is still finishing server-side
      // (e.g. GIF generation), switch to real polling instead of jumping to done.
      if (res.data.job_id && res.data.status && res.data.status !== 'done') {
        startJobPolling(res.data.job_id, STEPS_VIDEO.length);
        return;
      }
      clearAsync();
      setCurrentStep(STEPS_VIDEO.length);
      setTimeout(() => onCreated(res.data.article_id, res.data.video_path || '', res.data.job_id || ''), 500);
    } catch (err) {
      clearAsync();
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return; // user cancelled
      setError(failureMessage(err, 'Something went wrong'));
      setProcessing(false);
      setStillRunning(false);
      setCurrentStep(0);
    }
  };

  // Shared auto-processor for both Recorder and PhoneRecord callbacks
  const autoProcessVideo = (filePath, title) => {
    lastSubmitRef.current = () => autoProcessVideo(filePath, title); // GP-02: enable Retry
    setError(''); setRefusal(null);
    setProcessing(true);
    setStillRunning(false);
    setBgArticleId(null);
    setCurrentStep(0);
    clearAsync();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    api.post('/process-video', {
      video_path: filePath,
      title,
      transcript: '',
      detail_level: detailLevel,
      brand,
    }, { timeout: 600000, signal: ctrl.signal })
      .then(res => {
        if (res.data.article_id) setBgArticleId(res.data.article_id);
        if (res.data.job_id && res.data.status && res.data.status !== 'done') {
          startJobPolling(res.data.job_id, STEPS_VIDEO.length);
          return;
        }
        clearAsync();
        setCurrentStep(STEPS_VIDEO.length);
        setTimeout(() => onCreated(res.data.article_id, res.data.video_path || '', res.data.job_id || ''), 500);
      })
      .catch(err => {
        clearAsync();
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return; // user cancelled
        setError(failureMessage(err, 'Processing failed'));
        setProcessing(false);
        setStillRunning(false);
        setCurrentStep(0);
      });
  };

  // GP-02: combined upload + process flow (file-picker). Extracted from the
  // inline onChange so Retry can re-run it with the same File.
  const runUploadFlow = async (file) => {
    if (!file) return;
    lastSubmitRef.current = () => runUploadFlow(file); // enable Retry

    // Auto-fill title from filename
    const titleToUse = videoTitle || (() => {
      const name = file.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
      const t = name.charAt(0).toUpperCase() + name.slice(1);
      setVideoTitle(t);
      return t;
    })();

    // Enter processing overlay immediately — upload is step 0
    setError(''); setRefusal(null);
    setStillRunning(false);
    setBgArticleId(null);
    setUploadFlow(true);
    setProcessing(true);
    setCurrentStep(0);
    setUploadProgress(0);
    setUploadedName(file.name);
    clearAsync();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let uploadedPath = '';
    try {
      const form = new FormData();
      form.append('file', file);
      const uploadRes = await api.post('/upload-video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: ctrl.signal,
        onUploadProgress: (ev) => {
          if (ev.total) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
        },
      });
      uploadedPath = uploadRes.data.video_path;
      setVideoPath(uploadedPath);
    } catch (err) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return; // user cancelled
      setError('Upload failed: ' + (err.response?.data?.error || err.message));
      setProcessing(false);
      setStillRunning(false);
      setUploadFlow(false);
      setCurrentStep(0);
      return;
    }

    // Upload done — advance to transcription step and auto-process
    setCurrentStep(1);

    try {
      const res = await api.post('/process-video', {
        video_path: uploadedPath,
        title: titleToUse,
        transcript: videoTranscript.trim(),
        detail_level: detailLevel,
        brand,
      }, { timeout: 600000, signal: ctrl.signal });
      if (res.data.article_id) setBgArticleId(res.data.article_id);
      if (res.data.job_id && res.data.status && res.data.status !== 'done') {
        startJobPolling(res.data.job_id, STEPS_UPLOAD_VIDEO.length);
        return;
      }
      clearAsync();
      setCurrentStep(STEPS_UPLOAD_VIDEO.length);
      setTimeout(() => onCreated(res.data.article_id, res.data.video_path || '', res.data.job_id || ''), 500);
    } catch (err) {
      clearAsync();
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return; // user cancelled
      setError(failureMessage(err, 'Processing failed'));
      setProcessing(false);
      setStillRunning(false);
      setUploadFlow(false);
      setCurrentStep(0);
    }
  };

  const isLoomUrl = LOOM_RE.test(loomUrl.trim());
  const isVideoReady = videoPath.trim().length > 0;
  const steps = tab === 'loom' ? STEPS_LOOM : uploadFlow ? STEPS_UPLOAD_VIDEO : STEPS_VIDEO;
  // F04 — measured bytes are a real percentage; processing is indeterminate.
  // A stage index is never converted into a number.
  const uploadingBytes = uploadFlow && currentStep === 0 && uploadProgress > 0;
  const progressInfo = progressDisplay({
    phase: currentStep >= steps.length
      ? 'done'
      : jobPhase({ status: jobStatus, uploading: uploadingBytes }),
    uploadProgress,
    pollInterrupted,
  });
  // F04 — a stage reads Done only because the server reported being past it.
  const stepStates = stageStates({
    total: steps.length,
    confirmedIndex: currentStep,
    status: jobStatus,
  });

  // ── Review flow ────────────────────────────────────────────────────────
  // Lean Figma-only render. No source picker, no detail level (frames map
  // 1:1 to steps), no "How it works" card (documentation copy), and no
  // recording tabs — creating a review never generates documentation.
  if (isReviewFlow) {
    const breadcrumb = (
      <nav aria-label="Breadcrumb" className="breadcrumb">
        <Link to="/reviews">Design</Link>
        <span aria-hidden="true">/</span>
        {project?.slug && project?.title && (
          <>
            <Link to={`/reviews/${project.slug}`}>{project.title}</Link>
            <span aria-hidden="true">/</span>
          </>
        )}
        <span aria-current="page">New review</span>
      </nav>
    );
    return (
      <div className="page na-page--review">
        <PageHead
          title="New review"
          subtitle="Import Figma frames and share them for feedback."
          breadcrumb={breadcrumb}
        />
        <FigmaImportPanel onCreated={onCreated} project={project} />
      </div>
    );
  }

  // ── Documentation flow ────────────────────────────────────────────────
  // This is where product-persona Jira-as-input will land later (paste
  // ticket URL → prefill title + acceptance criteria before recording). New
  // source tabs slot into SOURCE_TABS; new pre-record context blocks fit
  // between the tab bar and the per-tab body.

  // Pre-submit validation for a picked/dropped file; only a valid file
  // reaches the existing upload + process flow.
  const acceptFile = (file) => {
    const problem = validateVideoFile(file);
    setFileError(problem);
    if (problem) return;
    runUploadFlow(file);
  };

  const onSourceTabKey = (e, i) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const n = SOURCE_TABS.length;
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
    setTab(SOURCE_TABS[next].id);
    document.getElementById(`na-tab-${SOURCE_TABS[next].id}`)?.focus();
  };

  // Hand-off from Recorder / PhoneRecord → auto-process. The ref blocks a
  // second hand-off during the 100 ms before the processing view mounts.
  const handOff = (filePath, title) => {
    if (handoffRef.current) return;
    handoffRef.current = true;
    setVideoPath(filePath);
    setVideoTitle(title);
    setTab('video');
    setTimeout(() => { handoffRef.current = false; autoProcessVideo(filePath, title); }, 100);
  };

  const fileName = videoPath ? videoPath.split('/').pop() : '';
  const sourceLabel = tab === 'record'
    ? (recordMode === 'phone' ? 'Phone recording via QR code' : 'Screen and voice, recorded in this browser')
    : tab === 'video'
      ? (fileName ? `Video file · ${fileName}` : 'Video file — MP4, MOV, M4V, AVI or WebM, up to 500 MB')
      : (isLoomUrl ? `Loom video · ${loomUrl.trim()}` : 'Loom share URL');

  const loomInvalid = loomTouched && loomUrl.trim().length > 0 && !isLoomUrl;
  const isDone = currentStep >= steps.length;

  // F05 — a job left running on a previous visit is offered back here.
  const _resumeCopy = resumeCopy(resumeHandle);

  return (
    <div className="page page--form">
      <PageHead
        title="New document"
        subtitle="Turn a screen recording into clear, step-by-step documentation."
      />

      {_resumeCopy && !processing && (
        <div className="inline-msg is-info" role="status">
          <Icon name="clock" />
          <div className="na-msg-body" style={{ flex: 1 }}>
            <strong>{_resumeCopy.title}</strong>
            <div>{_resumeCopy.body}</div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const h = resumeHandle;
              clearJobHandle(window.localStorage);
              setResumeHandle(null);
              if (h.articleId) onCreated(h.articleId, '', h.jobId || '');
              else if (h.jobId) { setProcessing(true); startJobPolling(h.jobId, steps.length); }
            }}
          >
            {_resumeCopy.openLabel}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => { clearJobHandle(window.localStorage); setResumeHandle(null); }}
          >
            {_resumeCopy.dismissLabel}
          </button>
        </div>
      )}

      {error && (
        <div className="inline-msg is-error" role="alert">
          <Icon name="alert" />
          <div className="na-msg-body">
            {/* §1.3.2 — a plan refusal is not a failure to fix; it is a limit
                to raise. Retry is pointless until the plan changes, so it is
                replaced by the upgrade contact. */}
            <p>
              <strong>{refusal ? 'This needs a bigger plan.' : 'Generation failed.'}</strong> {error}
            </p>
            <p>Your inputs are still here — nothing was started or charged.</p>
            {refusal ? (
              <a
                className="btn btn-secondary btn-sm"
                href={contactHref({ plan: planData?.plan, tenantId: planData?.tenant_id, reason: refusal.code })}
              >
                {refusal.action}
              </a>
            ) : lastSubmitRef.current && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => { const retry = lastSubmitRef.current; setError(''); setRefusal(null); if (retry) retry(); }}
              >
                <Icon name="refresh" /> Retry
              </button>
            )}
          </div>
        </div>
      )}

      {!processing ? (
        <>
          <section className="panel na-source" aria-label="Source">
            <div className="tablist" role="tablist" aria-label="Source method">
              {SOURCE_TABS.map((t, i) => (
                <button
                  key={t.id}
                  id={`na-tab-${t.id}`}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  aria-controls="na-source-panel"
                  tabIndex={tab === t.id ? 0 : -1}
                  onClick={() => setTab(t.id)}
                  onKeyDown={e => onSourceTabKey(e, i)}
                >
                  <Icon name={t.icon} size={18} />
                  {t.label}
                  {t.recommended && <span className="na-tab-tag">Recommended</span>}
                </button>
              ))}
            </div>

            <div id="na-source-panel" role="tabpanel" aria-labelledby={`na-tab-${tab}`} className="na-source-body">
              <p className="na-source-label">
                <span className="na-source-key">Source</span>
                <strong>{sourceLabel}</strong>
              </p>

              {/* Detail level — prefilled from Settings → Brand default. Sits
                  above the source because choosing a file or finishing a
                  recording starts generation straight away. */}
              <fieldset className="na-fieldset">
                <legend className="label">Detail level</legend>
                <div className="na-choices">
                  {DETAIL_OPTIONS.map(d => (
                    <label key={d.id} className={`na-choice ${detailLevel === d.id ? 'is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="na-detail-level"
                        value={d.id}
                        checked={detailLevel === d.id}
                        onChange={() => setDetailLevel(d.id)}
                      />
                      <span>
                        <span className="na-choice-title">{d.label}</span>
                        <span className="na-choice-desc">{d.desc}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Record tab */}
              {tab === 'record' && (
                <div className="na-stack">
                  {/* GP-15: Browser is the recommended primary path. Phone is
                      demoted to a secondary action rather than an equal toggle. */}
                  {recordMode === 'browser' ? (
                    <div className="na-mode-row">
                      <h2 className="na-mode-title"><Icon name="record" size={18} /> Record in browser</h2>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRecordMode('phone')}>
                        Record on your phone instead <Icon name="chevronRight" />
                      </button>
                    </div>
                  ) : (
                    <div className="na-mode-row">
                      <h2 className="na-mode-title"><Icon name="video" size={18} /> Record from phone</h2>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRecordMode('browser')}>
                        <Icon name="chevronLeft" /> Back to browser recording
                      </button>
                    </div>
                  )}

                  {recordMode === 'browser' && (
                    <Recorder
                      onRecordingReady={(filePath) => handOff(filePath, 'Screen Recording ' + new Date().toLocaleDateString())}
                    />
                  )}

                  {recordMode === 'phone' && (
                    <PhoneRecord
                      onVideoReady={(filePath) => handOff(filePath, 'Phone Recording ' + new Date().toLocaleDateString())}
                    />
                  )}
                </div>
              )}

              {/* Upload / local video */}
              {tab === 'video' && (
                <div className="na-stack">
                  {prefill && (
                    <div className="inline-msg is-info" role="status">
                      <Icon name="support" />
                      <div className="na-msg-body">
                        <strong>Pre-filled from Support Insights.</strong> Title and context have been added based on a recurring support issue. Add a video to continue.
                      </div>
                    </div>
                  )}

                  {whisperInstalled === false && (
                    <div className="inline-msg is-warning">
                      <Icon name="warning" />
                      <div className="na-msg-body">
                        <strong>Auto-transcription unavailable.</strong> Add an OpenAI API key in Settings to enable it, or paste your transcript manually below.
                      </div>
                    </div>
                  )}
                  {whisperInstalled === true && (
                    <span className="na-ok"><Icon name="checkCircle" size={18} /> <span>Auto-transcription ready</span></span>
                  )}

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="label" htmlFor="na-title">Title <span className="optional">(optional)</span></label>
                    <input
                      id="na-title"
                      className="input"
                      placeholder="e.g. How to create a new project"
                      value={videoTitle}
                      onChange={e => setVideoTitle(e.target.value)}
                      aria-describedby="na-title-hint"
                    />
                    <div className="field-hint" id="na-title-hint">If left blank, a chosen file names the draft; a typed path is saved as “Untitled recording”.</div>
                  </div>

                  <div
                    className={`na-drop ${dragOver ? 'is-over' : ''} ${fileError ? 'is-invalid' : ''}`}
                    onDragOver={e => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={e => {
                      e.preventDefault();
                      setDragOver(false);
                      acceptFile(e.dataTransfer?.files?.[0]);
                    }}
                  >
                    <Icon name="video" size={32} />
                    <div className="na-drop-title">Drop a video here</div>
                    <div className="na-text">or choose a file from your computer. MP4, MOV, M4V, AVI or WebM, up to 500 MB.</div>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => fileInputRef.current?.click()}
                      aria-describedby={fileError ? 'na-file-error' : undefined}
                    >
                      <Icon name="upload" /> Choose file
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={VIDEO_ACCEPT}
                      className="visually-hidden"
                      tabIndex={-1}
                      aria-hidden="true"
                      onChange={e => {
                        const file = e.target.files[0];
                        e.target.value = ''; // allow re-selecting the same file
                        acceptFile(file);
                      }}
                    />
                  </div>
                  {fileError && (
                    <div className="field-error" id="na-file-error" role="alert" style={{ marginTop: 0 }}>
                      <Icon name="alert" size={16} /> {fileError}
                    </div>
                  )}

                  <div className="na-divider">or</div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="label" htmlFor="na-video-path">Video path or URL</label>
                    <input
                      id="na-video-path"
                      className="input na-mono"
                      placeholder="/path/to/recording.mp4"
                      value={videoPath}
                      onChange={e => setVideoPath(e.target.value)}
                      aria-describedby="na-video-path-hint"
                    />
                    <div className="field-hint" id="na-video-path-hint">
                      {uploading ? (
                        <span className="na-busy"><Spinner size={14} /> Uploading {uploadedName}…</span>
                      ) : videoPath ? (
                        <span className="na-ok"><Icon name="checkCircle" size={16} /> <span>{fileName} is ready to process</span></span>
                      ) : (
                        'A path or URL the server can read. Choosing a file above fills this in and starts generating.'
                      )}
                    </div>
                  </div>

                  {/* Google Drive import — alternate source path (admin-only, flag-gated) */}
                  {DRIVE_IMPORT_ENABLED && (
                    <>
                      <div className="na-divider">or</div>
                      <DriveImportButton
                        disabled={uploading || processing}
                        onImported={(path, name) => {
                          setVideoPath(path);
                          setUploadedName(name);
                          if (!videoTitle) {
                            const base = name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' ');
                            setVideoTitle(base.charAt(0).toUpperCase() + base.slice(1));
                          }
                        }}
                      />
                    </>
                  )}

                  <div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-expanded={showVideoTranscript}
                      aria-controls="na-video-transcript"
                      onClick={() => setShowVideoTranscript(!showVideoTranscript)}
                    >
                      <Icon name="chevronDown" style={{ transform: showVideoTranscript ? 'rotate(180deg)' : undefined }} />
                      {showVideoTranscript ? 'Hide transcript' : 'Paste transcript instead'}
                    </button>
                  </div>

                  {showVideoTranscript && (
                    <div className="form-group" id="na-video-transcript" style={{ marginBottom: 0 }}>
                      <label className="label" htmlFor="na-video-transcript-input">Transcript <span className="optional">(optional)</span></label>
                      <textarea
                        id="na-video-transcript-input"
                        className="textarea"
                        placeholder="Paste transcript here. Include timestamps if available, e.g. [00:05] Click the settings button..."
                        rows={6}
                        value={videoTranscript}
                        onChange={e => setVideoTranscript(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="na-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!isVideoReady || uploading}
                      onClick={() => { if (!processing) handleProcessVideo(); }}
                      aria-describedby="na-video-action-note"
                    >
                      <Icon name="sparkle" /> {uploading ? 'Uploading…' : 'Generate documentation'}
                    </button>
                    <span className="meta" id="na-video-action-note">
                      {!isVideoReady
                        ? 'Choose a video file or enter a path first.'
                        : whisperInstalled ? 'Takes about 1–3 minutes (transcription + Flow).' : 'Takes about 15–30 seconds.'}
                    </span>
                  </div>
                </div>
              )}

              {/* Loom */}
              {tab === 'loom' && (
                <div className="na-stack">
                  <div className="inline-msg is-info">
                    <Icon name="alert" />
                    <div className="na-msg-body">
                      Loom video download requires a <strong>Business plan</strong>. Paste your transcript manually to generate docs without it.
                    </div>
                  </div>

                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="label" htmlFor="na-loom-url">Loom video URL</label>
                    <input
                      id="na-loom-url"
                      className="input"
                      type="url"
                      inputMode="url"
                      placeholder="https://www.loom.com/share/..."
                      value={loomUrl}
                      onChange={e => setLoomUrl(e.target.value)}
                      onBlur={() => setLoomTouched(true)}
                      aria-invalid={loomInvalid || undefined}
                      aria-describedby={loomInvalid ? 'na-loom-error' : 'na-loom-hint'}
                    />
                    {loomInvalid ? (
                      <div className="field-error" id="na-loom-error">
                        <Icon name="alert" size={16} /> Use a Loom share link, like https://www.loom.com/share/…
                      </div>
                    ) : (
                      <div className="field-hint" id="na-loom-hint">A loom.com/share/… or loom.com/v/… link.</div>
                    )}
                  </div>

                  <div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      aria-expanded={showLoomTranscript}
                      aria-controls="na-loom-transcript"
                      onClick={() => setShowLoomTranscript(!showLoomTranscript)}
                    >
                      <Icon name="chevronDown" style={{ transform: showLoomTranscript ? 'rotate(180deg)' : undefined }} />
                      {showLoomTranscript ? 'Hide transcript' : 'Paste transcript manually'}
                    </button>
                  </div>

                  {showLoomTranscript && (
                    <div className="form-group" id="na-loom-transcript" style={{ marginBottom: 0 }}>
                      <label className="label" htmlFor="na-loom-transcript-input">Transcript <span className="optional">(optional)</span></label>
                      <textarea
                        id="na-loom-transcript-input"
                        className="textarea"
                        placeholder="Open your Loom video → click Transcript on the right → copy all text here"
                        rows={6}
                        value={loomTranscript}
                        onChange={e => setLoomTranscript(e.target.value)}
                      />
                    </div>
                  )}

                  <div className="na-actions">
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={!isLoomUrl}
                      onClick={() => { if (!processing) handleProcessLoom(); }}
                      aria-describedby="na-loom-action-note"
                    >
                      <Icon name="sparkle" /> Generate documentation
                    </button>
                    <span className="meta" id="na-loom-action-note">
                      {isLoomUrl ? 'Flow structures the transcript into numbered steps.' : 'Enter a valid Loom share URL first.'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* How it works */}
          <section className="panel na-how" aria-labelledby="na-how-title">
            <h2 className="section-title" id="na-how-title">How it works</h2>
            <ol>
              {tab === 'record' && <>
                <li>Record your screen and voice directly in the browser</li>
                <li>Affixly transcribes the recording automatically with timestamps</li>
                <li>Flow structures it into numbered steps</li>
                <li>Adjust timestamps and generate GIFs per step</li>
                <li>Publish to Freshdesk, Confluence, or a Hosted Site</li>
              </>}
              {tab === 'video' && <>
                <li>Upload an MP4 or point to a local file path</li>
                <li>Affixly transcribes it automatically with timestamps</li>
                <li>Flow structures it into numbered steps</li>
                <li>Adjust timestamps and generate GIFs per step</li>
                <li>Publish to Freshdesk, Confluence, or a Hosted Site</li>
              </>}
              {tab === 'loom' && <>
                <li>Paste your Loom share URL</li>
                <li>Optionally paste the transcript to skip the Loom Business requirement</li>
                <li>Flow structures the transcript into numbered steps</li>
                <li>Adjust steps and add GIFs manually if needed</li>
                <li>Publish to Freshdesk, Confluence, or a Hosted Site</li>
              </>}
            </ol>
            <p className="field-hint">
              Got a long video covering multiple topics? Generate one doc, then use <strong>Split</strong> in the editor to carve it into linked sub-docs.
            </p>
          </section>
        </>
      ) : (
        /* Processing progress */
        <section className="panel na-progress" aria-labelledby="na-progress-title">
          <div>
            <h2 className="section-title" id="na-progress-title">
              {isDone ? 'Draft created' : 'Generating your draft'}
            </h2>
            <p className="na-source-label" style={{ marginTop: 4 }}>
              <span className="na-source-key">Source</span>
              <strong>{tab === 'loom' ? `Loom video · ${loomUrl.trim()}` : (uploadedName && uploadFlow ? `Video file · ${uploadedName}` : (fileName ? `Video file · ${fileName}` : 'Video'))}</strong>
            </p>
          </div>

          <div>
            {/* F04 — announce the named stage politely; never a ticking number. */}
            <p className="na-progress-now" aria-live="polite">
              {isDone ? 'Complete' : (progressInfo.mode === 'unknown' ? progressInfo.label : steps[currentStep]?.label)}
            </p>
            {progressInfo.mode === 'determinate' ? (
              <>
                <div
                  className="progress-bar"
                  role="progressbar"
                  aria-label="Generation progress"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={progressInfo.pct}
                  style={{ marginTop: 8 }}
                >
                  <div className="progress-fill" style={{ width: `${progressInfo.pct}%` }} />
                </div>
                <div className="meta" style={{ marginTop: 6 }}>{progressInfo.pct}%</div>
              </>
            ) : (
              <>
                {/* No aria-valuenow: how far along this is genuinely isn't known. */}
                <div
                  className={`progress-bar is-${progressInfo.mode}`}
                  role="progressbar"
                  aria-label="Generation progress"
                  aria-describedby="na-progress-note"
                  style={{ marginTop: 8 }}
                >
                  <div className="progress-fill" />
                </div>
                <div className="meta" id="na-progress-note" style={{ marginTop: 6 }}>{progressInfo.label}</div>
              </>
            )}
          </div>

          <ol className="na-steps">
            {steps.map((s, i) => {
              const state = stepStates[i] === 'active' ? 'current' : stepStates[i];
              return (
                <li key={s.id} className={`na-step is-${state}`}>
                  <span className="na-step-mark" aria-hidden="true">
                    {state === 'done' && <Icon name="check" size={14} />}
                    {state === 'current' && <Spinner size={12} />}
                  </span>
                  <span>{s.label}</span>
                  <span className="na-step-state">{state === 'done' ? 'Done' : state === 'current' ? 'In progress' : 'Waiting'}</span>
                </li>
              );
            })}
          </ol>

          {tab === 'video' && steps[currentStep]?.id === 'transcribe' && (
            <p className="field-hint" style={{ marginTop: 0 }}>
              Transcription time depends on video length. A 10-minute video takes about 1–2 minutes.
            </p>
          )}

          {/* GP-02d: honest "still running" hint when polling says the job
              hasn't finished yet. */}
          {stillRunning && !pollInterrupted && (
            <div className="inline-msg is-info" role="status">
              <Icon name="clock" />
              <div className="na-msg-body">Still processing — this can take a few minutes. You can continue in the background.</div>
            </div>
          )}

          {/* F04 — we cannot see the job right now. It is still running; say so
              plainly and keep the id so it can be picked up again. */}
          {pollInterrupted && (
            <div className="inline-msg is-warning" role="status">
              <Icon name="alert" />
              <div className="na-msg-body">
                Can't check progress right now — the connection to job status was interrupted.
                Processing has not been cancelled and will carry on.
                {activeJobId ? <> Job <code>{activeJobId}</code>.</> : null}
                {bgArticleId ? ' Your draft is already open-able below.' : ''}
              </div>
            </div>
          )}

          {/* GP-02b/c: don't hard-block. Continue in background once we know
              the article id; otherwise let them stop waiting (F05). */}
          <div className="na-progress-foot">
            {bgArticleId ? (
              <button type="button" className="btn btn-primary" onClick={continueInBackground}>
                {isDone ? 'Open draft' : 'Continue in background'} <Icon name="chevronRight" />
              </button>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={stopWaiting}>
                {stopWaitingCopy({ jobId: activeJobId, articleId: bgArticleId }).label}
              </button>
            )}
            <span className="meta">
              {bgArticleId
                ? 'Open the draft now — processing finishes on its own.'
                : stopWaitingCopy({ jobId: activeJobId, articleId: bgArticleId }).body}
            </span>
          </div>
        </section>
      )}
    </div>
  );
}
